import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, Search, Loader2, X, Navigation, Building2, Map, Hash } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Label } from '../ui/label';

/**
 * PlaceAutocomplete - Reusable address/place search component
 * 
 * Uses Google Places API via backend for:
 * - Autocomplete suggestions (addresses, cities, establishments)
 * - Place details with lat/lng
 * - CEP (postal code) lookup
 * 
 * @param {Object} props
 * @param {Object} props.value - Selected place object { place_id, name, formatted_address, lat, lng }
 * @param {Function} props.onChange - Callback when place is selected
 * @param {Function} props.onSearch - Optional callback when search button is clicked (for external search trigger)
 * @param {string} props.placeholder - Input placeholder
 * @param {string} props.types - Filter types: 'geocode', 'address', 'establishment', '(cities)', null for all
 * @param {Object} props.biasLocation - { lat, lng } to bias results near
 * @param {boolean} props.showMap - Show small map preview of selected location
 * @param {boolean} props.showCepButton - Show CEP lookup button (default: true)
 * @param {boolean} props.showSearchButton - Show search button next to clear (default: true)
 * @param {boolean} props.required - Required field
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.error - Error message to display
 */
export default function PlaceAutocomplete({
  value,
  onChange,
  onSearch,
  placeholder = "Digite um endereço, cidade ou local...",
  types = null, // null = busca tudo (endereços, cidades, estabelecimentos)
  biasLocation = null,
  showMap = false,
  showCepButton = true,
  showSearchButton = true,
  required = false,
  disabled = false,
  className = "",
  error = null,
  "data-testid": dataTestId = "place-autocomplete"
}) {
  const { api } = useAuth();
  const [inputValue, setInputValue] = useState(value?.name || value?.formatted_address || '');
  const [predictions, setPredictions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showCepModal, setShowCepModal] = useState(false);
  const [cepValue, setCepValue] = useState('');
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const dropdownRef = useRef(null);
  const cacheRef = useRef({}); // Cache for autocomplete results
  const lastQueryRef = useRef(''); // Track last query to avoid duplicates

  // Sync input value with external value changes
  useEffect(() => {
    if (value?.name || value?.formatted_address) {
      setInputValue(value.name || value.formatted_address);
    } else if (!value) {
      setInputValue('');
    }
  }, [value]);

  // Calculate dropdown position when opening or window resizes
  useEffect(() => {
    const updateDropdownPosition = () => {
      if (wrapperRef.current && isOpen) {
        const rect = wrapperRef.current.getBoundingClientRect();
        // Using fixed positioning, so use viewport-relative coordinates
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width
        });
      }
    };

    updateDropdownPosition();
    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition, true);
    
    // Also update when visualViewport changes (mobile keyboard)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateDropdownPosition);
      window.visualViewport.addEventListener('scroll', updateDropdownPosition);
    }
    
    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateDropdownPosition);
        window.visualViewport.removeEventListener('scroll', updateDropdownPosition);
      }
    };
  }, [isOpen, predictions]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both wrapper and dropdown
      const isOutsideWrapper = wrapperRef.current && !wrapperRef.current.contains(event.target);
      const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target);
      
      if (isOutsideWrapper && isOutsideDropdown) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch autocomplete predictions
  const fetchPredictions = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setPredictions([]);
      return;
    }

    // Skip if same query
    if (query === lastQueryRef.current) {
      return;
    }
    lastQueryRef.current = query;

    // Check cache first
    const cacheKey = query.toLowerCase().trim();
    if (cacheRef.current[cacheKey]) {
      setPredictions(cacheRef.current[cacheKey]);
      setIsOpen(true);
      setSelectedIndex(-1);
      return;
    }

    setIsLoading(true);
    try {
      const params = new URLSearchParams({ input: query });
      
      if (types) {
        params.append('types', types);
      }
      
      if (biasLocation?.lat && biasLocation?.lng) {
        params.append('lat', biasLocation.lat);
        params.append('lng', biasLocation.lng);
      }

      const response = await api.get(`/places/autocomplete?${params}`);
      const results = response.data || [];
      
      // Cache the results
      cacheRef.current[cacheKey] = results;
      
      setPredictions(results);
      setIsOpen(true);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Autocomplete error:', error);
      // Handle rate limiting (429)
      if (error.response?.status === 429) {
        toast.error('Muitas buscas. Aguarde um momento...');
      }
      setPredictions([]);
    } finally {
      setIsLoading(false);
    }
  }, [api, types, biasLocation]);

  // Debounced search
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // If cleared, notify parent
    if (!newValue) {
      onChange?.(null);
      setPredictions([]);
      setIsOpen(false);
      return;
    }

    // Debounce API call (500ms - increased to reduce rate limiting)
    debounceRef.current = setTimeout(() => {
      fetchPredictions(newValue);
    }, 500);
  };

  // Fetch place details and notify parent
  const selectPlace = async (prediction) => {
    setIsLoading(true);
    setIsOpen(false);
    setInputValue(prediction.description || prediction.main_text);

    try {
      const response = await api.get(`/places/details?place_id=${prediction.place_id}`);
      const placeDetails = response.data;

      onChange?.({
        place_id: placeDetails.place_id,
        name: placeDetails.name,
        formatted_address: placeDetails.formatted_address,
        address_components: placeDetails.address_components || [],  // Full Google address components
        lat: placeDetails.lat,
        lng: placeDetails.lng,
        types: placeDetails.types,
        phone: placeDetails.phone,
        website: placeDetails.website,
        rating: placeDetails.rating,
        google_maps_url: placeDetails.google_maps_url
      });

      setInputValue(placeDetails.name || placeDetails.formatted_address);
    } catch (error) {
      console.error('Place details error:', error);
      // Fallback to prediction data
      onChange?.({
        place_id: prediction.place_id,
        name: prediction.main_text,
        formatted_address: prediction.description,
        address_components: [],
        lat: null,
        lng: null,
        types: prediction.types
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Search by CEP
  const handleCepSearch = async () => {
    // Clean CEP - remove non-digits
    const cleanCep = cepValue.replace(/\D/g, '');
    
    if (cleanCep.length !== 8) {
      setCepError('CEP deve ter 8 dígitos');
      return;
    }

    setCepLoading(true);
    setCepError('');

    try {
      const response = await api.get(`/places/geocode?cep=${cleanCep}`);
      const location = response.data;

      if (location && location.lat && location.lng) {
        onChange?.({
          place_id: location.place_id,
          name: location.formatted_address,
          formatted_address: location.formatted_address,
          lat: location.lat,
          lng: location.lng,
          types: location.types || ['postal_code']
        });

        setInputValue(location.formatted_address);
        setShowCepModal(false);
        setCepValue('');
      } else {
        setCepError('CEP não encontrado');
      }
    } catch (error) {
      console.error('CEP lookup error:', error);
      setCepError('Erro ao buscar CEP. Verifique e tente novamente.');
    } finally {
      setCepLoading(false);
    }
  };

  // Format CEP as user types (00000-000)
  const handleCepChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 8) value = value.slice(0, 8);
    if (value.length > 5) {
      value = value.slice(0, 5) + '-' + value.slice(5);
    }
    setCepValue(value);
    setCepError('');
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || predictions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => 
          prev < predictions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          selectPlace(predictions[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  // Clear selection
  const handleClear = () => {
    setInputValue('');
    onChange?.(null);
    setPredictions([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  // Manual search trigger
  const handleManualSearch = () => {
    if (inputValue && inputValue.length >= 2) {
      fetchPredictions(inputValue);
    }
    // Also call external onSearch if provided
    if (onSearch) {
      onSearch();
    }
  };

  // Get icon for place type
  const getPlaceIcon = (placeTypes) => {
    if (!placeTypes || placeTypes.length === 0) return MapPin;
    if (placeTypes.includes('establishment') || placeTypes.includes('lodging') || placeTypes.includes('restaurant')) {
      return Building2;
    }
    if (placeTypes.includes('street_address') || placeTypes.includes('route')) {
      return MapPin;
    }
    if (placeTypes.includes('geocode') || placeTypes.includes('locality')) {
      return Map;
    }
    return MapPin;
  };

  return (
    <div 
      ref={wrapperRef} 
      className={cn("relative w-full", className)}
      data-testid={dataTestId}
    >
      {/* Input Field with Search/Clear and CEP Button */}
      <div className="relative flex gap-1.5">
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Search className="w-4 h-4" />
            )}
          </div>
          
          <Input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => predictions.length > 0 && setIsOpen(true)}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            className={cn(
              "pl-10 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500",
              inputValue ? "pr-16" : "pr-3",
              error && "border-red-500",
              disabled && "opacity-50 cursor-not-allowed"
            )}
            data-testid={`${dataTestId}-input`}
          />
          
          {/* Clear and Search buttons inside input */}
          {inputValue && !disabled && (
            <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="h-7 w-7 p-0 hover:bg-slate-700"
                title="Limpar"
                data-testid={`${dataTestId}-clear`}
              >
                <X className="w-4 h-4 text-slate-400" />
              </Button>
              {showSearchButton && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleManualSearch}
                  className="h-7 w-7 p-0 hover:bg-orange-500/20"
                  title="Buscar novamente"
                  data-testid={`${dataTestId}-search`}
                >
                  <Search className="w-4 h-4 text-orange-400" />
                </Button>
              )}
            </div>
          )}
        </div>

        {/* CEP Button */}
        {showCepButton && !disabled && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => setShowCepModal(true)}
            className="h-10 w-10 border-slate-600 bg-slate-800 hover:bg-slate-700 flex-shrink-0"
            title="Buscar por CEP"
            data-testid={`${dataTestId}-cep-btn`}
          >
            <Hash className="w-4 h-4 text-orange-400" />
          </Button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p className="text-xs text-red-400 mt-1">{error}</p>
      )}

      {/* Dropdown - Rendered via Portal to escape overflow:hidden containers */}
      {isOpen && predictions.length > 0 && typeof document !== 'undefined' && createPortal(
        <div 
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            zIndex: 2147483647, // Max z-index value to ensure it's always on top
            pointerEvents: 'auto'
          }}
          className="bg-slate-800 border border-slate-600 rounded-lg shadow-lg overflow-hidden max-h-64 overflow-y-auto"
          data-testid={`${dataTestId}-dropdown`}
          onMouseDown={(e) => e.stopPropagation()} // Stop propagation to prevent dialog from closing
          onTouchStart={(e) => e.stopPropagation()} // Stop touch propagation for mobile
        >
          {predictions.map((prediction, index) => {
            const Icon = getPlaceIcon(prediction.types);
            const isSelected = index === selectedIndex;

            return (
              <button
                key={prediction.place_id}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  selectPlace(prediction);
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  selectPlace(prediction);
                }}
                className={cn(
                  "w-full px-3 py-2 flex items-start gap-3 text-left transition-colors",
                  isSelected ? "bg-orange-600/20" : "hover:bg-slate-700"
                )}
                data-testid={`${dataTestId}-option-${index}`}
              >
                <Icon className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-white truncate">
                    {prediction.main_text}
                  </p>
                  <p className="text-xs text-slate-400 truncate">
                    {prediction.secondary_text}
                  </p>
                </div>
              </button>
            );
          })}
        </div>,
        document.body
      )}

      {/* No results - Also via Portal */}
      {isOpen && predictions.length === 0 && inputValue.length >= 2 && !isLoading && typeof document !== 'undefined' && createPortal(
        <div 
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            zIndex: 2147483647,
            pointerEvents: 'auto'
          }}
          className="bg-slate-800 border border-slate-600 rounded-lg p-4 text-center shadow-lg"
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <MapPin className="w-8 h-8 text-slate-500 mx-auto mb-2" />
          <p className="text-sm text-slate-400">Nenhum local encontrado</p>
          <p className="text-xs text-slate-500 mt-1">
            Tente buscar por CEP clicando no botão #
          </p>
        </div>,
        document.body
      )}

      {/* Selected location preview */}
      {showMap && value?.lat && value?.lng && (
        <div className="mt-2 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
          <div className="flex items-start gap-3">
            <Navigation className="w-5 h-5 text-orange-400 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-white">{value.name}</p>
              <p className="text-xs text-slate-400 truncate">{value.formatted_address}</p>
              <p className="text-xs text-slate-500 mt-1">
                Lat: {value.lat.toFixed(6)} | Lng: {value.lng.toFixed(6)}
              </p>
            </div>
            {value.google_maps_url && (
              <a
                href={value.google_maps_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-orange-400 hover:underline flex-shrink-0"
              >
                Ver no Maps
              </a>
            )}
          </div>
        </div>
      )}

      {/* CEP Modal */}
      <Dialog open={showCepModal} onOpenChange={setShowCepModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Hash className="w-5 h-5 text-orange-500" />
              Buscar por CEP
            </DialogTitle>
            <DialogDescription>
              Digite o CEP para encontrar o endereço automaticamente
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="cep">CEP</Label>
              <Input
                id="cep"
                placeholder="00000-000"
                value={cepValue}
                onChange={handleCepChange}
                onKeyPress={(e) => e.key === 'Enter' && handleCepSearch()}
                maxLength={9}
                className="text-lg tracking-wider"
                data-testid={`${dataTestId}-cep-input`}
                autoFocus
              />
              {cepError && (
                <p className="text-xs text-red-400">{cepError}</p>
              )}
              <p className="text-xs text-slate-500">
                Exemplo: 01310-100 (Av. Paulista, São Paulo)
              </p>
            </div>

            <Button
              type="button"
              onClick={handleCepSearch}
              disabled={cepLoading || cepValue.replace(/\D/g, '').length !== 8}
              className="w-full bg-orange-500 hover:bg-orange-600"
              data-testid={`${dataTestId}-cep-search-btn`}
            >
              {cepLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Buscando...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Buscar Endereço
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/**
 * PlaceAutocompleteSimple - Minimal version for forms (without CEP button)
 * Returns only essential data: place_id, name, formatted_address, lat, lng
 */
export function PlaceAutocompleteSimple(props) {
  return <PlaceAutocomplete {...props} showMap={false} showCepButton={false} />;
}
